import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import { fleetStateColor } from '@/utils/fleetMarker'

// Várias telas carregam as posições ao abrir; com a integração fora do ar o aviso sai no máximo a cada 5 min.
const FLEET_WARNING_INTERVAL_MS = 5 * 60 * 1000
let lastFleetWarningAt = 0


/**
 * Ultimas posicoes das viaturas do no, para o mapa.
 *
 * Duas fontes que se completam: a carga inicial pelo GET /posicoes, porque
 * quem abre a tela nao pode esperar ate 30s pela proxima leva; e o canal
 * `node.{id}`, que a partir dai atualiza sozinho.
 */
export default {
  mixins: [ApiErrorMixin],

  data() {
    return {
      fleetPositions: [],
      fleetFetchedAt: null,
      fleetStaleAfter: 180,
      loadingFleetPositions: false,
      // true quando a última carga falhou: a integração com o Ares Frotas está fora do ar.
      fleetUnavailable: false,
      fleetChannelName: null,
      fleetListener: null,
      fleetStaleTimer: null,
      fleetTeamsReloadTimer: null,
    }
  },

  computed: {
    /** Só o que dá para desenhar: sem coordenada não há pino. */
    fleetPositionsOnMap() {
      return this.fleetPositions.filter(p => p.lat !== null && p.lng !== null)
    },
  },

  methods: {
    async loadFleetPositions() {
      this.loadingFleetPositions = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}posicoes`)
        this.fleetPositions = res.data.positions || []
        this.fleetFetchedAt = res.data.fetched_at || null
        this.fleetStaleAfter = res.data.stale_after_seconds || 180
        this.fleetUnavailable = false
      } catch (e) {
        this.fleetPositions = []
        this.fleetUnavailable = true
        if (this.isFleetIntegrationDown(e)) {
          this.warnFleetUnavailable()
        } else {
          this.$message('Erro', this.apiErrorText(e, 'Não foi possível carregar as posições das equipes.'), 'error')
        }
      } finally {
        this.loadingFleetPositions = false
      }
    },

    /** 502/503/504 ou sem resposta: a integração (Redis das posições ou Ares Frotas) está fora do ar. */
    isFleetIntegrationDown(error) {
      const status = error && error.response ? error.response.status : null

      return !status || [502, 503, 504].includes(status)
    },

    /** Texto amigável para o operador quando a integração está fora; senão, o que o servidor mandou. */
    fleetErrorText(error, fallback) {
      return this.isFleetIntegrationDown(error) ? this.$t('fleet.unavailable') : this.apiErrorText(error, fallback)
    },

    warnFleetUnavailable() {
      const now = Date.now()
      if (now - lastFleetWarningAt < FLEET_WARNING_INTERVAL_MS) return

      lastFleetWarningAt = now
      this.$message(this.$t('fleet.unavailableTitle'), this.$t('fleet.unavailable'), 'warning')
    },

    watchFleetPositions() {
      if (typeof window.Echo === 'undefined') return
      if (this.fleetListener) return

      const node = this.$store.state.selected_node
      if (!node || !node.id) return

      this.fleetChannelName = `node.${node.id}`
      this.fleetListener = payload => this.onFleetPositions(payload)

      window.Echo.private(this.fleetChannelName)
        .listen('.VehiclePositions', this.fleetListener)

      // O servidor decide o `stale` na leitura, mas uma tela aberta parada
      // precisa envelhecer sozinha.
      this.fleetStaleTimer = setInterval(() => this.refreshFleetStale(), 30000)
    },

    unwatchFleetPositions() {
      if (this.fleetTeamsReloadTimer) {
        clearTimeout(this.fleetTeamsReloadTimer)
        this.fleetTeamsReloadTimer = null
      }
      if (this.fleetStaleTimer) {
        clearInterval(this.fleetStaleTimer)
        this.fleetStaleTimer = null
      }

      if (typeof window.Echo === 'undefined' || !this.fleetChannelName) return

      // stopListening e nao leave(): o TeamRequestsMixin assina o mesmo canal,
      // e sair dele aqui derrubaria o alerta de solicitação de equipe junto.
      window.Echo.private(this.fleetChannelName)
        .stopListening('.VehiclePositions', this.fleetListener)

      this.fleetChannelName = null
      this.fleetListener = null
    },

    onFleetPositions(payload) {
      const chegando = payload && payload.positions
      if (!chegando) return

      let codigoNovo = false

      Object.keys(chegando).forEach(code => {
        const nova = chegando[code]
        const i = this.fleetPositions.findIndex(p => p.identifier_code === code)
        if (i < 0) codigoNovo = true

        const registro = {
          identifier_code: code,
          lat: nova.lat,
          lng: nova.lng,
          speed: nova.speed,
          ignition: nova.ignition,
          at: nova.at,
          age_seconds: 0,
          stale: false,
          // O broadcast não carrega a equipe; conserva a que veio da carga
          // inicial em vez de apagar o nome do pino a cada leva.
          team: i >= 0 ? this.fleetPositions[i].team : null,
          // Idem o tipo/imagem do veículo: vem da carga inicial, não do broadcast.
          vehicle_type: i >= 0 ? this.fleetPositions[i].vehicle_type : null,
        }

        if (i >= 0) this.$set(this.fleetPositions, i, registro)
        else this.fleetPositions.push(registro)
      })

      // Viatura que só agora começou a reportar não veio na carga inicial, e o
      // broadcast não traz a equipe: sem recarregar, o pino ficaria como
      // "código sem equipe" até alguém reabrir a tela.
      if (codigoNovo) this.scheduleFleetTeamsReload()

      if (typeof this.onFleetPositionsApplied === 'function') {
        this.onFleetPositionsApplied(Object.keys(chegando))
      }
    },

    // Junta várias viaturas novas na mesma leva em uma só carga. Depois dela o
    // código já está na lista (com ou sem equipe), então não entra em laço.
    scheduleFleetTeamsReload() {
      if (this.fleetTeamsReloadTimer) return

      this.fleetTeamsReloadTimer = setTimeout(() => {
        this.fleetTeamsReloadTimer = null
        this.loadFleetPositions()
      }, 1500)
    },

    refreshFleetStale() {
      const agora = Date.now()

      this.fleetPositions.forEach((p, i) => {
        if (!p.at) return
        const idade = Math.floor((agora - new Date(p.at).getTime()) / 1000)
        if (idade === p.age_seconds) return

        this.$set(this.fleetPositions, i, {
          ...p,
          age_seconds: idade,
          stale: idade > this.fleetStaleAfter,
        })
      })
    },

    fleetColor(position) {
      return fleetStateColor(position)
    },

    fleetStateLabel(position) {
      if (!position.team) return 'código sem equipe'
      return position.team.busy ? 'em atendimento' : 'livre'
    },

    fleetMarkerTitle(position) {
      const equipe = position.team ? position.team.name : 'sem equipe vinculada'
      return `${position.identifier_code} · ${equipe} · ${this.fleetStateLabel(position)} · ${this.fleetAgeLabel(position)}`
    },

    /**
     * Distância em linha reta, em km. É o que desenha o raio: instantânea e de
     * graça. A distância por rota vem do Google e custa cota — as duas sempre
     * divergem, e a tela precisa dizer qual está mostrando.
     */
    fleetStraightLineKm(lat1, lng1, lat2, lng2) {
      const dLat = (lat2 - lat1) * 111.32
      const dLng = (lng2 - lng1) * 111.32 * Math.cos(((lat1 + lat2) / 2) * (Math.PI / 180))
      return Math.sqrt(dLat * dLat + dLng * dLng)
    },

    /** "há 4 min" — só aparece no pino quando a leitura já envelheceu. */
    fleetAgeLabel(position) {
      const s = position.age_seconds
      if (s === null || s === undefined) return 'sem horário'
      if (s < 60) return `há ${s}s`
      return `há ${Math.floor(s / 60)} min`
    },
  },

  beforeDestroy() {
    this.unwatchFleetPositions()
  },
}
